import React, { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import LoginModal from './components/LoginModal';
import NoteEditor from './components/NoteEditor';
import NoteList from './components/NoteList';
import PrivateChatDialog from './components/PrivateChatDialog';
import { 
  generateKeyPair, 
  loadKeysFromStorage, 
  saveKeysToStorage, 
  rsaEncrypt, 
  rsaDecrypt
} from './utils/crypto';
import { authAPI, autoReAuthenticate, checkTokenExpiry } from './utils/api';
import { WS_URL } from './config';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userKeys, setUserKeys] = useState(null);
  const [currentChat, setCurrentChat] = useState('public');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [serverPublicKey, setServerPublicKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReAuthenticating, setIsReAuthenticating] = useState(false);
  const [reauthMessage, setReauthMessage] = useState('');
  
  // 笔记相关状态
  const [currentView, setCurrentView] = useState('chat'); // 'chat' or 'notes'
  const [editingNote, setEditingNote] = useState(null);
  const [noteRefreshTrigger, setNoteRefreshTrigger] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // 私聊相关状态
  const [selectedPrivateChat, setSelectedPrivateChat] = useState(null);
  const [privateChatRefreshTrigger, setPrivateChatRefreshTrigger] = useState(0);
  
  // WebSocket连接状态
  const [wsConnection, setWsConnection] = useState(null);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // 强制重新计算布局
      document.body.style.transform = 'translateZ(0)';
      setTimeout(() => {
        document.body.style.transform = '';
      }, 0);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // WebSocket连接管理
  useEffect(() => {
    if (isLoggedIn && userKeys?.token) {
      const connectWebSocket = () => {
        const wsUrl = `${WS_URL}?token=${userKeys.token}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          setWsConnection(ws);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            // 处理私聊消息通知
            if (message.type === 'private_message_notification') {
              // 刷新私聊列表和当前私聊对话框
              setPrivateChatRefreshTrigger(prev => prev + 1);
            }
          } catch (error) {
            // 静默处理解析错误
          }
        };

        ws.onclose = (event) => {
          setWsConnection(null);
          // 尝试重连
          setTimeout(() => {
            connectWebSocket();
          }, 3000);
        };

        ws.onerror = (error) => {
          // 静默处理WebSocket错误
        };

        return ws;
      };

      const ws = connectWebSocket();
      return () => {
        if (ws) {
          ws.close();
        }
      };
    }
  }, [isLoggedIn, userKeys?.token]);

  // 定期检查并修复布局（防止消息加载后布局被破坏）
  useEffect(() => {
    const layoutCheckInterval = setInterval(() => {
      const appElement = document.querySelector('.app');
      if (appElement) {
        const currentWidth = window.innerWidth;
        const isMobile = currentWidth <= 768;
        const currentFlexDirection = window.getComputedStyle(appElement).flexDirection;
        
        // 检查布局是否正确
        if (isMobile && currentFlexDirection !== 'column') {
          // 强制重新计算布局
          appElement.style.transform = 'translateZ(0)';
          setTimeout(() => {
            appElement.style.transform = '';
          }, 0);
        } else if (!isMobile && currentFlexDirection !== 'row') {
          // 强制重新计算布局
          appElement.style.transform = 'translateZ(0)';
          setTimeout(() => {
            appElement.style.transform = '';
          }, 0);
        }
      }
    }, 3000); // 每3秒检查一次

    return () => clearInterval(layoutCheckInterval);
  }, []);

  useEffect(() => {
    // 获取服务器公钥
    loadServerPublicKey();
    
    // 尝试从本地存储加载密钥
    const savedKeys = loadKeysFromStorage();
    if (savedKeys && savedKeys.accessToken) {
      setUserKeys(savedKeys);
      setIsLoggedIn(true);
    } else {
      setShowLoginModal(true);
    }
  }, []);

  // 定期检查Token过期
  useEffect(() => {
    if (!isLoggedIn || !userKeys) return;

    const checkTokenInterval = setInterval(async () => {
      if (checkTokenExpiry(userKeys.accessToken)) {
        setIsReAuthenticating(true);
        setReauthMessage('正在自动重新认证...');
        
        try {
          const updatedUserKeys = await autoReAuthenticate(userKeys);
          setUserKeys(updatedUserKeys);
          setReauthMessage('自动重新认证成功');
          
          // 3秒后清除消息
          setTimeout(() => {
            setReauthMessage('');
          }, 3000);
        } catch (error) {
          setReauthMessage('自动重新认证失败，请重新登录');
          setIsReAuthenticating(false);
          
          // 5秒后显示登录模态框
          setTimeout(() => {
            handleLogout();
            setShowLoginModal(true);
          }, 5000);
        } finally {
          setIsReAuthenticating(false);
        }
      }
    }, 60000); // 每分钟检查一次

    return () => clearInterval(checkTokenInterval);
  }, [isLoggedIn, userKeys]);

  const loadServerPublicKey = async () => {
    try {
      const response = await authAPI.getServerPublicKey();
      if (response.success) {
        setServerPublicKey(response.public_key);
      }
    } catch (error) {
    }
  };

  const handleLogin = async (keys) => {
    if (!serverPublicKey) {
      alert('服务器公钥未加载，请重试');
      return;
    }

    setIsLoading(true);
    try {
      
      // 1. 用户注册 - 直接传递用户公钥
      const registerResponse = await authAPI.register(keys.publicKey);
      if (!registerResponse.success) {
        throw new Error(registerResponse.data?.message || '注册失败');
      }

      const userId = registerResponse.data.user_id;

      // 2. 请求挑战
      const encryptedUserId = rsaEncrypt(userId, serverPublicKey);
      if (!encryptedUserId) {
        throw new Error('加密用户ID失败');
      }

      const challengeResponse = await authAPI.requestChallenge(encryptedUserId);
      if (!challengeResponse.success) {
        throw new Error(challengeResponse.message || '请求挑战失败');
      }

      const { encrypted_challenge, challenge_id } = challengeResponse.data;

      // 3. 解密挑战
      const challenge = rsaDecrypt(encrypted_challenge, keys.privateKey);
      if (!challenge) {
        throw new Error('解密挑战失败');
      }

      // 4. 加密响应
      const encryptedResponse = rsaEncrypt(challenge, serverPublicKey);
      if (!encryptedResponse) {
        throw new Error('加密响应失败');
      }

      // 5. 验证挑战
      const verifyResponse = await authAPI.verifyChallenge(encryptedUserId, encryptedResponse, challenge_id);
      if (!verifyResponse.success) {
        throw new Error(verifyResponse.message || '验证挑战失败');
      }

      const { access_token, refresh_token, encrypted_user_key } = verifyResponse.data;

      // 6. 解密用户密钥
      const userSecretKey = rsaDecrypt(encrypted_user_key, keys.privateKey);
      if (!userSecretKey) {
        throw new Error('解密用户密钥失败');
      }

      // 7. 保存认证信息
      const authData = {
        ...keys,
        userId: userId,
        accessToken: access_token,
        refreshToken: refresh_token,
        userSecretKey: encrypted_user_key, // 保存加密后的密钥，不是解密后的
        serverPublicKey: serverPublicKey
      };

      setUserKeys(authData);
      setIsLoggedIn(true);
      setShowLoginModal(false);
      saveKeysToStorage(authData);
      
    } catch (error) {
      alert('认证失败: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setUserKeys(null);
    setIsLoggedIn(false);
    setCurrentChat('public');
    localStorage.removeItem('toxchat_keys');
  };

  const handleGenerateKeys = (keys) => {
    if (keys) {
      handleLogin(keys);
    } else {
      // 如果没有传入密钥，则生成新的
      const newKeys = generateKeyPair();
      handleLogin(newKeys);
    }
  };

  const handleImportKeys = (importedKeys) => {
    handleLogin(importedKeys);
  };

  // 笔记相关处理函数
  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'chat') {
      setEditingNote(null);
    }
  };

  const handleNoteSelect = (note) => {
    setEditingNote({
      id: note.note_id,
      title: note.title,
      content: note.content,
      isEditing: true
    });
  };

  const handleNoteEdit = (noteId, title, content) => {
    setEditingNote({
      id: noteId,
      title: title,
      content: content,
      isEditing: true
    });
  };

  const handleNoteSave = (savedNote) => {
    setEditingNote(null);
    setNoteRefreshTrigger(prev => prev + 1);
  };

  const handleNoteCancel = () => {
    setEditingNote(null);
  };

  const handleNoteDelete = (noteId) => {
    setNoteRefreshTrigger(prev => prev + 1);
  };

  const handleCreateNote = () => {
    setEditingNote({
      id: null,
      title: '',
      content: '',
      isEditing: false
    });
  };

  // 私聊相关处理函数
  const handlePrivateChatSelect = (chat) => {
    setSelectedPrivateChat(chat);
  };

  const handlePrivateChatClose = () => {
    setSelectedPrivateChat(null);
    // 切换到群聊
    setCurrentChat('public');
  };

  const handlePrivateChatDeleted = (chatID) => {
    setSelectedPrivateChat(null);
    setPrivateChatRefreshTrigger(prev => prev + 1);
  };

  const handlePrivateChatRefresh = () => {
    setPrivateChatRefreshTrigger(prev => prev + 1);
  };

  if (!isLoggedIn) {
    return (
      <LoginModal
        isOpen={showLoginModal}
        onGenerateKeys={handleGenerateKeys}
        onImportKeys={handleImportKeys}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="app" key={`app-${windowWidth}`}>
      {/* 重新认证状态提示 */}
      {(isReAuthenticating || reauthMessage) && (
        <div className="reauth-notification">
          <div className="reauth-content">
            {isReAuthenticating && <div className="spinner"></div>}
            <span>{reauthMessage}</span>
          </div>
        </div>
      )}
      
      <Sidebar
        currentChat={currentChat}
        onChatSelect={setCurrentChat}
        onLogout={handleLogout}
        userKeys={userKeys}
        currentView={currentView}
        onViewChange={handleViewChange}
        onCreateNote={handleCreateNote}
        onPrivateChatSelect={handlePrivateChatSelect}
        privateChatRefreshTrigger={privateChatRefreshTrigger}
      />
      {currentView === 'chat' ? (
        <div className="chat-main">
          {selectedPrivateChat ? (
            <PrivateChatDialog
              chat={selectedPrivateChat}
              currentUserID={userKeys?.userId}
              userPrivateKey={userKeys?.privateKey}
              onClose={handlePrivateChatClose}
              onChatDeleted={handlePrivateChatDeleted}
              userKeys={userKeys}
              refreshTrigger={privateChatRefreshTrigger}
            />
          ) : (
            <ChatArea
              currentChat={currentChat}
              userKeys={userKeys}
              serverPublicKey={serverPublicKey}
            />
          )}
        </div>
      ) : (
        <div className="notes-container">
          <div className="notes-layout">
            <div className="notes-sidebar">
              <NoteList
                userKeys={userKeys}
                serverPublicKey={serverPublicKey}
                onNoteSelect={handleNoteSelect}
                onNoteEdit={handleNoteEdit}
                onNoteDelete={handleNoteDelete}
                refreshTrigger={noteRefreshTrigger}
                editingNoteId={editingNote?.id}
              />
            </div>
            <div className="notes-main">
              {editingNote ? (
                <NoteEditor
                  noteId={editingNote.id}
                  userKeys={userKeys}
                  serverPublicKey={serverPublicKey}
                  onSave={handleNoteSave}
                  onCancel={handleNoteCancel}
                  initialTitle={editingNote.title}
                  initialContent={editingNote.content}
                  isEditing={editingNote.isEditing}
                />
              ) : (
                <div className="notes-welcome">
                  <h2>Welcome to Notes</h2>
                  <p>Select a note from the left sidebar to start editing, or create a new note.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
